import { useState, useCallback, useMemo } from 'react';
import { config } from '../../../config';
import { getToken } from '../../../utils/token';
import { flattenBroadcastResponse } from '../../../utils/datacenter';
import { apiClient } from '../../../lib/apiClient';
import type { Division } from '../../UserDetail/types';
import type { DivisionWithPotentialBid } from '../utils/templateRenderer';

interface OfferSuggestion {
  takerUuid: string;
  marketUuid: string;
  desiredOffer: number;
  bidType: 'DISC' | 'APR';
}

interface SimulatedOffer {
  marketUuid: string;
  takerUuid: string;
  maxBid: number;
  discountPercent: number;
  discount: number;
  discountedAmount: number;
}

interface UseOfferSimulatorOptions {
  divisions: Division[];
}

interface UseOfferSimulatorReturn {
  divisionsWithBids: DivisionWithPotentialBid[];
  setDivisionsWithBids: React.Dispatch<React.SetStateAction<DivisionWithPotentialBid[]>>;
  globalPotentialBid: { maxBid: number | null; isDiscountBidding: boolean };
  setGlobalPotentialBid: React.Dispatch<React.SetStateAction<{ maxBid: number | null; isDiscountBidding: boolean }>>;
  simulateOffersLoading: boolean;
  offerSuggestionsLoading: boolean;
  simulationIsDirty: boolean;
  setSimulationIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
  showMissingOfferSuggestionsWarning: boolean;
  hasCheckableRows: boolean;
  enabledDivisions: DivisionWithPotentialBid[];
  allDivisionsEnabled: boolean;
  divisionsWithBidsReady: DivisionWithPotentialBid[];
  handleSetAllPotentialBids: () => void;
  handleGetOfferSuggestions: () => Promise<void>;
  handleSimulateOffers: () => Promise<void>;
  handleDivisionBidChange: (
    divisionKey: string,
    field: 'maxBid' | 'isDiscountBidding',
    value: number | boolean | null,
  ) => void;
  handleDivisionEnabledChange: (index: number, enabled: boolean) => void;
  handleToggleAll: () => void;
}

export const useOfferSimulator = ({ divisions }: UseOfferSimulatorOptions): UseOfferSimulatorReturn => {
  const [divisionsWithBids, setDivisionsWithBids] = useState<DivisionWithPotentialBid[]>(() => {
    return divisions
      .filter((d) => d.marketId)
      .map((division) => ({
        ...division,
        potentialBid: {
          maxBid: null,
          isDiscountBidding: false,
          enabled: division.eligibleInvoiceAmount > 0,
        },
      }));
  });

  const [globalPotentialBid, setGlobalPotentialBid] = useState<{ maxBid: number | null; isDiscountBidding: boolean }>({
    maxBid: null,
    isDiscountBidding: false,
  });

  const [simulateOffersLoading, setSimulateOffersLoading] = useState(false);
  const [offerSuggestionsLoading, setOfferSuggestionsLoading] = useState(false);
  const [simulationIsDirty, setSimulationIsDirty] = useState(false);
  const [showMissingOfferSuggestionsWarning, setShowMissingOfferSuggestionsWarning] = useState(false);

  const enabledDivisions = useMemo(() => {
    return divisionsWithBids.filter((d) => d.potentialBid.enabled && d.marketIsEnabled);
  }, [divisionsWithBids]);

  const allDivisionsEnabled = useMemo(() => {
    const eligibleDivisions = divisionsWithBids.filter((d) => d.marketIsEnabled && d.eligibleInvoiceAmount > 0);
    if (eligibleDivisions.length === 0) return false;
    return eligibleDivisions.every((d) => d.potentialBid.enabled);
  }, [divisionsWithBids]);

  const divisionsWithBidsReady = useMemo(() => {
    return enabledDivisions.filter((d) => d.potentialBid.maxBid !== null && d.potentialBid.maxBid > 0);
  }, [enabledDivisions]);

  const hasCheckableRows = useMemo(() => {
    return divisionsWithBids.some((d) => d.marketIsEnabled && d.eligibleInvoiceAmount > 0);
  }, [divisionsWithBids]);

  const handleSetAllPotentialBids = useCallback(() => {
    if (globalPotentialBid.maxBid === null) {
      return;
    }

    setDivisionsWithBids((prev) =>
      prev.map((division) => {
        if (division.marketIsEnabled && division.potentialBid.enabled) {
          return {
            ...division,
            potentialBid: {
              ...division.potentialBid,
              maxBid: globalPotentialBid.maxBid,
              isDiscountBidding: globalPotentialBid.isDiscountBidding,
            },
          };
        }

        return division;
      }),
    );
    setSimulationIsDirty(true);
  }, [globalPotentialBid]);

  const handleGetOfferSuggestions = useCallback(async () => {
    const checkableDivisions = divisionsWithBids.filter(
      (d) => d.marketIsEnabled && d.eligibleInvoiceAmount > 0 && d.takerUuid != null && d.takerUuid !== '',
    );
    if (checkableDivisions.length === 0) {
      throw new Error(
        'No rows can be selected. Rows are selectable only when the market is enabled and the division has eligible invoice amount (DPE) greater than zero.',
      );
    }

    const takerUuids = [...new Set(checkableDivisions.map((d) => d.takerUuid as string))];
    setShowMissingOfferSuggestionsWarning(false);
    setOfferSuggestionsLoading(true);

    try {
      const queryString = takerUuids.map((uuid) => `filter[takerUuid]=${encodeURIComponent(uuid)}`).join('&');
      const api = apiClient();
      const responseData = await api
        .get(`broadcast/api/admin/insights/offer_suggestions?${queryString}`)
        .json<
          OfferSuggestion[] | { data?: OfferSuggestion[]; suggestions?: OfferSuggestion[]; results?: OfferSuggestion[] }
        >();
      const raw = Array.isArray(responseData)
        ? responseData
        : (responseData?.data ?? responseData?.suggestions ?? responseData?.results ?? []);
      const suggestions = flattenBroadcastResponse<OfferSuggestion>(raw);

      setDivisionsWithBids((prev) =>
        prev.map((division) => {
          const suggestion = suggestions.find(
            (s) => s.takerUuid === division.takerUuid && s.marketUuid === division.marketUuid,
          );
          if (!suggestion || !division.marketIsEnabled) return division;
          const canEnable = division.eligibleInvoiceAmount > 0;
          return {
            ...division,
            potentialBid: {
              ...division.potentialBid,
              maxBid: suggestion.desiredOffer,
              isDiscountBidding: suggestion.bidType === 'DISC',
              enabled: canEnable ? true : division.potentialBid.enabled,
            },
          };
        }),
      );

      const requestedCount = checkableDivisions.length;
      const matchedCount = suggestions.filter((s) =>
        checkableDivisions.some((d) => d.takerUuid === s.takerUuid && d.marketUuid === s.marketUuid),
      ).length;
      setShowMissingOfferSuggestionsWarning(requestedCount > 0 && matchedCount < requestedCount);
      setSimulationIsDirty(true);
    } catch (err) {
      setShowMissingOfferSuggestionsWarning(false);
      throw err;
    } finally {
      setOfferSuggestionsLoading(false);
    }
  }, [divisionsWithBids]);

  const handleSimulateOffers = useCallback(async () => {
    const offers = enabledDivisions
      .map((division) => ({
        marketUuid: division.marketUuid,
        takerUuid: division.takerUuid,
        maxBid: Math.round(100 * (division.potentialBid.maxBid || 0)) / 100,
        isDiscountBidding: division.potentialBid.isDiscountBidding,
      }))
      .filter((offer) => offer.maxBid > 0);

    if (offers.length === 0) {
      return;
    }

    setSimulateOffersLoading(true);

    try {
      const url = `${config.GATEWAY_URL}/broadcast/api/admin/insights/simulate-taker-offers`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(offers),
      });

      if (!response.ok) {
        throw new Error('Failed to simulate offers');
      }

      const responseData = await response.json();
      const simulatedOffers: SimulatedOffer[] = Array.isArray(responseData) ? responseData : responseData?.data || [];

      if (!Array.isArray(simulatedOffers)) {
        throw new Error('Invalid response format: expected array of simulated offers');
      }

      const simulatedMap = new Map<string, SimulatedOffer>();
      simulatedOffers.forEach((offer) => {
        const key = `${offer.marketUuid}-${offer.takerUuid}`;
        simulatedMap.set(key, {
          ...offer,
          discountPercent: offer.discountPercent / 100,
        });
      });

      setDivisionsWithBids((prev) =>
        prev.map((division) => {
          const key = `${division.marketUuid}-${division.takerUuid}`;
          const simulated = simulatedMap.get(key);
          if (simulated) {
            return {
              ...division,
              simulation: {
                maxBid: simulated.maxBid,
                discount: simulated.discount,
                discountPercent: simulated.discountPercent,
                discountedAmount: simulated.discountedAmount,
              },
            };
          }

          return division;
        }),
      );

      setSimulationIsDirty(false);
    } catch (error) {
      throw error;
    } finally {
      setSimulateOffersLoading(false);
    }
  }, [enabledDivisions]);

  const handleDivisionBidChange = useCallback(
    (divisionKey: string, field: 'maxBid' | 'isDiscountBidding', value: number | boolean | null) => {
      setDivisionsWithBids((prev) =>
        prev.map((division) => {
          const key = `${division.marketUuid}-${division.takerUuid}`;
          if (key === divisionKey) {
            return {
              ...division,
              potentialBid: {
                ...division.potentialBid,
                [field]: value,
              },
            };
          }

          return division;
        }),
      );
      setSimulationIsDirty(true);
    },
    [],
  );

  const handleDivisionEnabledChange = useCallback((index: number, enabled: boolean) => {
    setDivisionsWithBids((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        potentialBid: {
          ...updated[index].potentialBid,
          enabled,
        },
      };
      return updated;
    });
    setSimulationIsDirty(true);
  }, []);

  const handleToggleAll = useCallback(() => {
    const newEnabled = !allDivisionsEnabled;
    setDivisionsWithBids((prev) =>
      prev.map((division) => {
        const isEligible = division.marketIsEnabled && division.eligibleInvoiceAmount > 0;
        return {
          ...division,
          potentialBid: {
            ...division.potentialBid,
            enabled: isEligible ? newEnabled : division.potentialBid.enabled,
          },
        };
      }),
    );
    setSimulationIsDirty(true);
  }, [allDivisionsEnabled]);

  return {
    divisionsWithBids,
    setDivisionsWithBids,
    globalPotentialBid,
    setGlobalPotentialBid,
    simulateOffersLoading,
    offerSuggestionsLoading,
    simulationIsDirty,
    setSimulationIsDirty,
    showMissingOfferSuggestionsWarning,
    hasCheckableRows,
    enabledDivisions,
    allDivisionsEnabled,
    divisionsWithBidsReady,
    handleSetAllPotentialBids,
    handleGetOfferSuggestions,
    handleSimulateOffers,
    handleDivisionBidChange,
    handleDivisionEnabledChange,
    handleToggleAll,
  };
};
